import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OpsService } from '../../core/services/ops.service';
import { RbacService } from '../../core/services/rbac.service';

type UserModalMode = 'create' | 'edit';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent {
  private readonly ops = inject(OpsService);
  private readonly rbac = inject(RbacService);
  readonly users = this.ops.adminUsers;
  readonly message = signal<string | null>(null);
  readonly busy = signal(false);
  readonly modalOpen = signal(false);
  readonly modalMode = signal<UserModalMode>('create');

  canManageUsers(): boolean {
    return this.rbac.canModule('settings');
  }

  /** Assignable desk roles — loaded from API (fallback defaults). All desks are 1:1. */
  deskRoles: string[] = ['dm', 'officer', 'peshkar', 'records_officer'];
  newUsername = '';
  newPassword = '';
  newDisplayName = '';
  newEmail = '';
  newPhone = '';
  newRole = 'officer';
  newDepartment = 'DM Office';

  readonly editingId = signal<string | number | null>(null);
  editName = '';
  editEmail = '';
  editPhone = '';
  editDepartment = '';
  editRole = 'officer';
  editPassword = '';

  constructor() {
    void this.ops.listAllRoles().then((roles) => {
      // User management needs every desk role, including `dm`.
      // `/admin/roles?assignable=true` intentionally hides `dm` (complaint Assign UI).
      const names = roles
        .map((r) => r.roleName)
        .filter((name) => name && name !== 'admin' && name !== 'citizen');
      if (names.length) {
        this.deskRoles = names;
        if (!this.deskRoles.includes(this.newRole)) this.newRole = this.deskRoles[0];
      }
    }).catch(() => undefined);
  }

  roleLabel(u: { role: string; roles?: string[] }): string {
    return u.role || (u.roles?.length ? u.roles[0] : 'unassigned');
  }

  exclusiveTakenBy(role: string, exceptUserId?: string | number | null): string | null {
    if (!this.deskRoles.includes(role)) return null;
    const holder = this.users().find((u) => u.role === role && u.id !== exceptUserId);
    return holder ? holder.name : null;
  }

  createRoleOptions(): Array<{ value: string; label: string; disabled: boolean }> {
    return this.deskRoles.map((r) => {
      const taken = this.exclusiveTakenBy(r);
      return {
        value: r,
        label: taken ? `${r} (held by ${taken})` : r,
        disabled: !!taken,
      };
    });
  }

  editRoleOptions(): Array<{ value: string; label: string; disabled: boolean }> {
    const editing = this.editingId();
    return this.deskRoles.map((r) => {
      const taken = this.exclusiveTakenBy(r, editing);
      return {
        value: r,
        label: taken ? `${r} (held by ${taken} — will transfer)` : r,
        disabled: false,
      };
    });
  }

  private validateContactFields(email: string, phone: string): string | null {
    const e = email.trim();
    const p = phone.trim();
    if (e && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      return 'Enter a valid email address';
    }
    if (p) {
      const digits = p.replace(/\D/g, '');
      if (digits.length < 10 || digits.length > 15) {
        return 'Enter a valid mobile number (10–15 digits)';
      }
    }
    return null;
  }

  openCreate(): void {
    if (!this.canManageUsers()) {
      this.message.set('Only Admin can create desk profiles.');
      return;
    }
    this.modalMode.set('create');
    this.editingId.set(null);
    this.newUsername = '';
    this.newPassword = '';
    this.newDisplayName = '';
    this.newEmail = '';
    this.newPhone = '';
    this.newRole = this.deskRoles.find((r) => !this.exclusiveTakenBy(r)) || this.deskRoles[0] || 'officer';
    this.newDepartment = 'DM Office';
    this.message.set(null);
    this.modalOpen.set(true);
  }

  startEdit(u: {
    id: string | number;
    name: string;
    email: string;
    phone: string;
    department: string;
    role: string;
  }): void {
    if (!this.canManageUsers() || u.role === 'admin') return;
    this.modalMode.set('edit');
    this.editingId.set(u.id);
    this.editName = u.name;
    this.editEmail = u.email || '';
    this.editPhone = u.phone || '';
    this.editDepartment = u.department || 'DM Office';
    this.editRole = u.role && this.deskRoles.includes(u.role) ? u.role : 'officer';
    this.editPassword = '';
    this.message.set(null);
    this.modalOpen.set(true);
  }

  closeModal(force = false): void {
    if (this.busy() && !force) return;
    this.modalOpen.set(false);
    this.editingId.set(null);
    this.editPassword = '';
  }

  async saveEdit(): Promise<void> {
    if (!this.canManageUsers()) {
      this.message.set('Only Admin can edit profiles.');
      return;
    }
    const id = this.editingId();
    if (!id) return;
    if (!this.editName.trim()) {
      this.message.set('Display name is required.');
      return;
    }
    const contactErr = this.validateContactFields(this.editEmail, this.editPhone);
    if (contactErr) {
      this.message.set(contactErr);
      return;
    }
    this.busy.set(true);
    try {
      const patch: {
        fullName: string;
        department: string;
        role: string;
        password?: string;
        contactEmail: string | null;
        contactPhone: string | null;
      } = {
        fullName: this.editName.trim(),
        department: this.editDepartment,
        role: this.editRole,
        contactEmail: this.editEmail.trim().toLowerCase() || null,
        contactPhone: this.editPhone.trim() || null,
      };
      if (this.editPassword.trim()) patch.password = this.editPassword.trim();
      this.message.set(await this.ops.updateDeskUser(id, patch));
      this.closeModal(true);
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Update failed');
    } finally {
      this.busy.set(false);
    }
  }

  async removeUser(id: string | number, name: string): Promise<void> {
    if (!this.canManageUsers()) {
      this.message.set('Only Admin can remove profiles.');
      return;
    }
    if (name === 'System Admin' || id === 'U-OFF-04') {
      this.message.set('Cannot remove the system admin account.');
      return;
    }
    if (!confirm(`Remove profile “${name}”? Their desk role will be freed.`)) return;
    try {
      if (this.editingId() === id) this.closeModal();
      this.message.set(await this.ops.removeDeskUser(id));
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Remove failed');
    }
  }

  async createUser(): Promise<void> {
    if (!this.canManageUsers()) {
      this.message.set('Only Admin can create desk profiles.');
      return;
    }
    if (!this.newUsername.trim()) {
      this.message.set('Username is required.');
      return;
    }
    if (!this.newPassword) {
      this.message.set('Password is required.');
      return;
    }
    const contactErr = this.validateContactFields(this.newEmail, this.newPhone);
    if (contactErr) {
      this.message.set(contactErr);
      return;
    }
    const taken = this.exclusiveTakenBy(this.newRole);
    if (taken) {
      this.message.set(
        `Role “${this.newRole}” is already held by ${taken}. Only one person can have this role.`,
      );
      return;
    }
    this.busy.set(true);
    try {
      this.message.set(
        await this.ops.createDeskUser({
          username: this.newUsername.trim(),
          password: this.newPassword,
          displayName: this.newDisplayName.trim() || this.newUsername.trim(),
          role: this.newRole,
          department: this.newDepartment,
          email: this.newEmail.trim() || undefined,
          phone: this.newPhone.trim() || undefined,
        }),
      );
      this.closeModal(true);
      this.newUsername = '';
      this.newPassword = '';
      this.newDisplayName = '';
      this.newEmail = '';
      this.newPhone = '';
      this.newRole = this.deskRoles[0] || 'officer';
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Create failed');
    } finally {
      this.busy.set(false);
    }
  }
}
